import React, { useState, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Modal,
  FlatList,
  ScrollView,
} from '../components/rn';
import PrimaryActionButton from '../components/PrimaryActionButton';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, fontSize, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { api, ApiError, NetworkError } from '../services/api';
import { COUNTRIES, Country } from '../data/countries';
import { getPhoneCountryOptions } from '../data/phoneCountrySearch';
import { getDeviceRegion } from '../utils/locale';
import Logo from '../components/Logo';
import GoogleLoginAction from '../components/GoogleLoginAction';
import PasskeySignInAction from '../components/PasskeySignInAction';

// Cihazın bölgesine göre varsayılan ülke (küresel ürün — sabit +90 yok).
//
// D-427 — yedek artık "listenin ilki" değil. Liste alfabetik olduğu için ilk sıra
// rastgele bir ülkeye (Afganistan) denk gelirdi; öncesinde ise Türkiye'ye denk geliyordu ve
// küresel bir üründe tek bir pazarı varsayılan yapıyordu. Yedek açıkça şirketin yargı
// çevresidir (`LEGAL.md` — UK Ltd) ve yalnız cihaz bölgesi okunamadığında devreye girer.
const FALLBACK_ISO = 'GB';

function defaultCountry(): Country {
  const region = getDeviceRegion();
  return COUNTRIES.find((c) => c.iso === region)
    ?? COUNTRIES.find((c) => c.iso === FALLBACK_ISO)
    ?? COUNTRIES[0];
}

type Props = {
  onContinue: (phone: string) => void;
  onVerify?: (token: string, onboarded: boolean) => void | Promise<void>;
};

export default function PhoneScreen({ onContinue, onVerify }: Props) {
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [country, setCountry] = useState<Country>(defaultCountry);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const activeMethod = useRef<'sms' | 'google' | 'passkey' | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Yerel numara sadece rakam; baştaki 0'lar atılır (uluslararası biçim).
  const localDigits = phone.replace(/\D/g, '').replace(/^0+/, '');
  const fullPhone = country.dial + localDigits;
  const canContinue = localDigits.length >= 6 && !loading && !googleBusy;

  async function handleContinue() {
    if (activeMethod.current) return;
    activeMethod.current = 'sms';
    setLoading(true);
    try {
      await api.sendOtp(fullPhone);
      onContinue(fullPhone);
    } catch (error) {
      if (error instanceof NetworkError) {
        Alert.alert(t('message.noConnection'), t('message.checkInternet'));
      } else {
        Alert.alert(t('common.error'), t(error instanceof ApiError && error.status === 429
          ? 'otp.rateLimited' : 'phone.sendFailed'));
      }
    } finally {
      activeMethod.current = null;
      setLoading(false);
    }
  }

  const filtered = useMemo(() => getPhoneCountryOptions(lang, search), [lang, search]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={styles.inner}>
        <Logo size={64} color={colors.black} style={{ marginBottom: spacing.sm }} />
        <Text style={styles.title}>nosubject</Text>
        <Text style={styles.subtitle}>{t('phone.subtitle')}</Text>

        <View style={styles.phoneRow}>
          <TouchableOpacity
            testID="phone-country-picker"
            style={styles.countryBtn}
            onPress={() => { setSearch(''); setPickerOpen(true); }}>
            <Text style={styles.countryFlag}>{country.flag}</Text>
            <Text style={styles.countryDial}>{country.dial}</Text>
            <Text style={styles.countryCaret}>▾</Text>
          </TouchableOpacity>
          <TextInput
            style={styles.input}
            placeholder="555 123 4567"
            placeholderTextColor={colors.gray400}
            keyboardType="number-pad"
            value={phone}
            onChangeText={(v) => setPhone(v.replace(/[^0-9]/g, ''))}
            autoFocus
          />
        </View>

        <PrimaryActionButton
          testID="onboarding-phone-continue"
          label={t('common.continue')}
          disabled={!canContinue}
          loading={loading}
          onPress={handleContinue}
        />
        <GoogleLoginAction onVerify={onVerify} disabled={loading}
          tryStart={() => {
            if (activeMethod.current) return false;
            activeMethod.current = 'google';
            setGoogleBusy(true);
            return true;
          }}
          onFinish={() => { activeMethod.current = null; setGoogleBusy(false); }} />
        {/* D-882 — Face ID, Touch ID or a fingerprint, through a passkey. */}
        <PasskeySignInAction onVerify={onVerify} disabled={loading}
          tryStart={() => {
            if (activeMethod.current) return false;
            activeMethod.current = 'passkey';
            setGoogleBusy(true);
            return true;
          }}
          onFinish={() => { activeMethod.current = null; setGoogleBusy(false); }} />
        </View>
      </ScrollView>

      {Platform.OS === 'web' && (
        <TouchableOpacity
          style={styles.footer}
          onPress={() => (globalThis as any).open?.('/mimari', '_blank')}>
          <Text style={styles.footerLink}>{t('phone.architectureLink')}</Text>
        </TouchableOpacity>
      )}

      <Modal visible={pickerOpen} animationType="slide" onRequestClose={() => setPickerOpen(false)}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('phone.selectCountry')}</Text>
            <TouchableOpacity onPress={() => setPickerOpen(false)}>
              <Text style={styles.modalCancel}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <TextInput
            testID="phone-country-search"
            style={styles.modalSearch}
            placeholder={t('phone.countrySearch')}
            placeholderTextColor={colors.gray400}
            value={search}
            onChangeText={setSearch}
            autoCorrect={false}
            autoFocus
          />
          <FlatList
            data={filtered}
            keyExtractor={(item) => item.iso}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingHorizontal: spacing.lg }}
            renderItem={({ item }) => (
              <TouchableOpacity
                testID={`phone-country-${item.iso}`}
                style={styles.countryRow}
                onPress={() => { setCountry(item); setPickerOpen(false); }}>
                <Text style={styles.rowFlag}>{item.flag}</Text>
                <Text style={styles.rowName}>{item.localizedName}</Text>
                <Text style={styles.rowDial}>{item.dial}</Text>
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />
        </KeyboardAvoidingView>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollContent: { flexGrow: 1 },
  inner: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl * 3,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: '700',
    color: colors.black,
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: fontSize.md,
    color: colors.gray600,
    marginBottom: spacing.xl,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: spacing.xl,
  },
  countryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingRight: spacing.md,
    marginRight: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
  },
  countryFlag: { fontSize: fontSize.lg, marginRight: 6 },
  countryDial: { fontSize: fontSize.lg, color: colors.black, fontWeight: '600' },
  countryCaret: { fontSize: fontSize.sm, color: colors.gray600, marginLeft: 4 },
  input: {
    flex: 1,
    fontSize: fontSize.lg,
    color: colors.black,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
    paddingVertical: spacing.sm,
  },
  footer: {
    paddingBottom: spacing.lg,
    alignItems: 'center',
  },
  footerLink: {
    fontSize: fontSize.sm,
    color: colors.gray600,
    textDecorationLine: 'underline',
  },
  modal: { flex: 1, backgroundColor: colors.white },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
  },
  modalTitle: { fontSize: fontSize.lg, fontWeight: '700', color: colors.black },
  modalCancel: { fontSize: fontSize.md, color: colors.gray600 },
  modalSearch: {
    fontSize: fontSize.md,
    color: colors.black,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray200,
    paddingVertical: spacing.sm,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  countryRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.md },
  rowFlag: { fontSize: fontSize.lg, marginRight: spacing.md },
  rowName: { flex: 1, fontSize: fontSize.md, color: colors.black },
  rowDial: { fontSize: fontSize.md, color: colors.gray600, fontWeight: '500' },
  separator: { height: 1, backgroundColor: colors.gray100 },
});
